import { memo, useEffect, useState } from "react";

import { getAttachmentBlob } from "lib/fleetMissionStorage/FleetMissionStorageClient";

/**
 * Renders one imported attachment in fleet mode.
 *
 * Direct ARC mode puts its token in the query string and hands the URL to
 * `<img src>` (see `buildArcStorageUrl`). The fleet read API authenticates on
 * the Authorization header alone, so the bytes are fetched here and shown from
 * a blob object URL instead. They come from the imported copy rather than from
 * the ARC, so they still render when the robot is offline.
 *
 * Two module-level maps, both keyed by (serial, attachmentId):
 *
 *  - `blobCache` holds resolved object URLs with a reference count, so a
 *    thumbnail, the Photos grid, and the lightbox share one blob. The URL is
 *    revoked when the last consumer unmounts — a run view holds dozens of
 *    thumbnails and switches runs freely, so leaking them is not theoretical.
 *  - `inFlight` deduplicates concurrent requests. Its AbortController is
 *    reference-counted too: aborting on the first unmount would cancel a fetch
 *    that other mounted consumers are still awaiting.
 */

type CacheEntry = { url: string; refCount: number };
type PendingEntry = {
    promise: Promise<Blob | null>;
    controller: AbortController;
    waiters: number;
};

const blobCache = new Map<string, CacheEntry>();
const inFlight = new Map<string, PendingEntry>();

const ATTACHMENT_RETRY_MS = 5000;
const IN_PROGRESS_FETCH_STATUSES = new Set(["waiting_source", "pending", "fetching"]);

const cacheKey = (serial: string, attachmentId: string) => `${serial}::${attachmentId}`;

const retain = (key: string): string | null => {
    const entry = blobCache.get(key);
    if (!entry) return null;
    entry.refCount += 1;
    return entry.url;
};

const release = (key: string): void => {
    const entry = blobCache.get(key);
    if (!entry) return;
    entry.refCount -= 1;
    if (entry.refCount <= 0) {
        URL.revokeObjectURL(entry.url);
        blobCache.delete(key);
    }
};

const startOrJoin = (
    key: string,
    proxyHostname: string | null | undefined,
    serial: string,
    attachmentId: string,
): PendingEntry => {
    const existing = inFlight.get(key);
    if (existing) {
        existing.waiters += 1;
        return existing;
    }

    const controller = new AbortController();
    const promise = getAttachmentBlob(
        proxyHostname,
        serial,
        attachmentId,
        controller.signal,
    ).then((blob) => {
        if (blob) {
            // refCount starts at zero: every consumer retains explicitly.
            blobCache.set(key, { url: URL.createObjectURL(blob), refCount: 0 });
        }
        return blob;
    });

    const entry: PendingEntry = { promise, controller, waiters: 1 };
    inFlight.set(key, entry);
    promise
        .catch(() => undefined)
        .finally(() => {
            if (inFlight.get(key) === entry) inFlight.delete(key);
        });
    return entry;
};

const leave = (key: string, entry: PendingEntry): void => {
    entry.waiters -= 1;
    if (entry.waiters <= 0) {
        entry.controller.abort();
        if (inFlight.get(key) === entry) inFlight.delete(key);
    }
};

export type FleetAttachment = {
    id: string;
    fileName: string;
    mimeType: string;
    size: number | null;
    fetchStatus?: string;
    available?: boolean;
};

interface FleetAttachmentImageProps {
    proxyHostname: string | null | undefined;
    sourceArcSerial: string | null | undefined;
    attachment: FleetAttachment;
    className?: string;
    onClick?: () => void;
}

const FleetAttachmentImage: React.FC<FleetAttachmentImageProps> = ({
    proxyHostname,
    sourceArcSerial,
    attachment,
    className = "",
    onClick,
}) => {
    const [url, setUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    // `available` is false while the importer is still copying. The API answers
    // 409 for those, but the read API does not push attachment-state changes to
    // this page, so retry the transient states until the copied bytes appear.
    // Terminal states do not retry forever.
    const isReady = attachment.available !== false;
    const isPending = attachment.available === false
        && IN_PROGRESS_FETCH_STATUSES.has(attachment.fetchStatus ?? "");

    useEffect(() => {
        if ((!isReady && !isPending) || !sourceArcSerial || !proxyHostname) {
            setUrl(null);
            setIsLoading(false);
            setFailed(false);
            return;
        }

        const key = cacheKey(sourceArcSerial, attachment.id);
        const cached = retain(key);
        if (cached) {
            setUrl(cached);
            setIsLoading(false);
            setFailed(false);
            return () => release(key);
        }

        let isCancelled = false;
        let retryTimer: number | null = null;
        let retained = false;
        let pending: PendingEntry | null = null;

        const load = () => {
            if (isCancelled) return;

            pending = startOrJoin(
                key,
                proxyHostname,
                sourceArcSerial,
                attachment.id,
            );

            setUrl(null);
            setIsLoading(true);
            setFailed(false);

            pending.promise
                .then((blob) => {
                    pending = null;
                    if (isCancelled) return;
                    if (!blob && isPending) {
                        setIsLoading(false);
                        retryTimer = window.setTimeout(load, ATTACHMENT_RETRY_MS);
                        return;
                    }
                    setIsLoading(false);
                    if (!blob) {
                        // 404: visible row, but the copied blob is unavailable.
                        setFailed(true);
                        return;
                    }
                    const resolved = retain(key);
                    if (!resolved) return;
                    retained = true;
                    setUrl(resolved);
                })
                .catch(() => {
                    pending = null;
                    if (isCancelled) return;
                    if (isPending) {
                        setIsLoading(false);
                        retryTimer = window.setTimeout(load, ATTACHMENT_RETRY_MS);
                        return;
                    }
                    setIsLoading(false);
                    setFailed(true);
                });
        };

        load();

        return () => {
            isCancelled = true;
            if (retryTimer !== null) window.clearTimeout(retryTimer);
            if (pending) leave(key, pending);
            if (retained) release(key);
        };
    }, [proxyHostname, sourceArcSerial, attachment.id, isReady, isPending]);

    if (url) {
        return (
            <img
                src={url}
                alt={attachment.fileName}
                className={className}
                onClick={onClick}
            />
        );
    }

    const label = isLoading
        ? "Loading"
        : isPending
          ? "Uploading to cloud"
          : failed || attachment.available === false
            ? "Not available"
            : "No preview";

    return (
        <div
            className={`flex items-center justify-center bg-black90 ${className}`}
            onClick={onClick}
        >
            <p className="text-h5 text-black40 italic">{label}</p>
        </div>
    );
};

export default memo(FleetAttachmentImage);
